import { watchEffect, toValue, type MaybeRefOrGetter } from 'vue';
import { useRoute } from 'vue-router';
import { usePageHeaderStore, type BreadcrumbItem } from '@/stores/page-header-store';

export interface PageBreadcrumbsOptions {
    breadcrumbs: MaybeRefOrGetter<BreadcrumbItem[]>;
    /** Osmos help article key. When set, a help (?) trigger appears next to the breadcrumbs. */
    helpKey?: MaybeRefOrGetter<string | null | undefined>;
}

export function usePageBreadcrumbs(breadcrumbs: MaybeRefOrGetter<BreadcrumbItem[]>): void;
export function usePageBreadcrumbs(options: PageBreadcrumbsOptions): void;
export function usePageBreadcrumbs(
    arg: MaybeRefOrGetter<BreadcrumbItem[]> | PageBreadcrumbsOptions,
): void {
    const options: PageBreadcrumbsOptions = isOptions(arg) ? arg : { breadcrumbs: arg };
    const store = usePageHeaderStore();
    const route = useRoute();
    watchEffect(() => {
        store.breadcrumbs = toValue(options.breadcrumbs);
        // Reset to null when a page doesn't pass a key, so help from a
        // previously visited page never leaks onto the current one.
        store.helpKey = toValue(options.helpKey) ?? null;
        // Stamp the owning route so PageHeader can tell a live trail from one
        // left over by the previous page (#715).
        store.owner = routeKey(route.name);
    });
}

/**
 * Route names are `string | symbol | undefined`; normalise to the string form the
 * store compares on. Shared with page-header.vue so both sides key identically.
 */
export function routeKey(name: unknown): string | null {
    return typeof name === 'string' ? name : null;
}

/** A crumb ready for BaseBreadcrumb, whose `to` is required. */
export interface ResolvedBreadcrumb extends BreadcrumbItem {
    to: string;
}

/** The two values PageHeader renders: what the trail is, and whether a `?` shows. */
export interface PageHeaderContent {
    breadcrumbs: ResolvedBreadcrumb[];
    helpKey: string | null;
}

/**
 * Decides what the header shows for `route`, given what the store currently holds.
 *
 * Lives here rather than inline in page-header.vue so it can be tested against a real
 * router: as long as it was a pair of computeds inside the SFC, nothing could import
 * it, and a test could only re-implement it — which passes just as happily when the
 * real gate is deleted.
 *
 * `owner !== null` is load-bearing: routeKey collapses "no name" to null, so without
 * it two DIFFERENT anonymous routes recognise each other as owner and the second
 * shows the first's trail, with the gate vouching for it.
 */
export function pageHeaderContent(
    store: { owner: string | null; breadcrumbs: BreadcrumbItem[]; helpKey: string | null },
    route: { name?: unknown; meta: { title?: unknown } },
): PageHeaderContent {
    const owns = store.owner !== null && store.owner === routeKey(route.name);
    if (owns && store.breadcrumbs.length) {
        return {
            breadcrumbs: store.breadcrumbs.map(b => ({ ...b, to: b.to ?? '#' })),
            helpKey: store.helpKey,
        };
    }
    // No trail of its own: the page still names itself from meta.title — the same
    // string the browser tab already shows — rather than another page's trail.
    return {
        breadcrumbs: typeof route.meta.title === 'string'
            ? [{ title: route.meta.title, disabled: true, to: '#' }]
            : [],
        helpKey: null,
    };
}

function isOptions(
    arg: MaybeRefOrGetter<BreadcrumbItem[]> | PageBreadcrumbsOptions,
): arg is PageBreadcrumbsOptions {
    // The breadcrumbs value is always an array, a getter (function), or a ref —
    // none of which carry a `breadcrumbs` own property, so it discriminates the
    // options object unambiguously.
    return typeof arg === 'object' && arg !== null && !Array.isArray(arg) && 'breadcrumbs' in arg;
}
